import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Form, ListGroup, Modal, Spinner } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { documentService } from '../../services/document.service'
import { toastService } from '../../services/toast.service'
import { Document, DocumentRelatedItem } from '../../types'
import { formatDate } from '../../utils/dateFormatter'

interface RelatedItemsSectionProps {
  documentId: number
  canEdit: boolean
}

export const RelatedItemsSection: React.FC<RelatedItemsSectionProps> = ({ documentId, canEdit }) => {
  const { t } = useTranslation()
  const [items, setItems] = useState<DocumentRelatedItem[]>([])
  const [loading, setLoading] = useState(false)
  const [filterQuery, setFilterQuery] = useState('')
  const [saving, setSaving] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [modalSearchQuery, setModalSearchQuery] = useState('')
  /** API pretraga tek nakon pauze u kucanju (ne na svaki karakter). */
  const [debouncedModalQuery, setDebouncedModalQuery] = useState('')
  const [modalResults, setModalResults] = useState<Document[]>([])
  const [modalLoading, setModalLoading] = useState(false)
  const [selectedInModal, setSelectedInModal] = useState<Document | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null)
  const [downloadingId, setDownloadingId] = useState<number | null>(null)

  // Normalizujemo ID-jeve na brojeve (API payload ponekad stiže kao string).
  const excludedDocumentIds = useMemo(() => {
    // Isključujemo trenutni dokument i sve već povezane dokumente
    // (relacije se serviraju neusmereno, pa je jedna lista dovoljna).
    const ids = new Set<number>([Number(documentId)])
    for (const item of items) ids.add(Number(item.document_id))
    return ids
  }, [documentId, items])

  const filteredItems = useMemo(() => {
    if (!filterQuery.trim()) return items
    const q = filterQuery.trim().toLowerCase()
    return items.filter(
      (item) =>
        (item.title || '').toLowerCase().includes(q) ||
        String(item.document_id).includes(q)
    )
  }, [items, filterQuery])

  const loadRelatedItems = useCallback(
    async (showSpinner = true) => {
      if (showSpinner) setLoading(true)
      try {
        const related = await documentService.getRelatedItems(documentId)
        setItems(related)
      } catch (error) {
        toastService.showError(t('error_loading'), error)
      } finally {
        if (showSpinner) setLoading(false)
      }
    },
    [documentId, t]
  )

  useEffect(() => {
    void loadRelatedItems()
  }, [loadRelatedItems])

  useEffect(() => {
    const refreshIfVisible = () => {
      if (document.visibilityState !== 'visible') return
      void loadRelatedItems(false)
    }

    window.addEventListener('focus', refreshIfVisible)
    window.addEventListener('pageshow', refreshIfVisible)
    document.addEventListener('visibilitychange', refreshIfVisible)

    return () => {
      window.removeEventListener('focus', refreshIfVisible)
      window.removeEventListener('pageshow', refreshIfVisible)
      document.removeEventListener('visibilitychange', refreshIfVisible)
    }
  }, [loadRelatedItems])

  const loadModalResults = useCallback(
    async (query: string) => {
      setModalLoading(true)
      try {
        const trimmed = query.trim()
        const filterRules = trimmed.length >= 2 ? [{ rule_type: 0, value: trimmed }] : []
        const response = await documentService.listFiltered(1, 20, 'created', true, filterRules, {
          truncate_content: true,
          fields: 'id,title,created',
        })
        const filtered = response.results.filter((doc) => !excludedDocumentIds.has(Number(doc.id)))
        setModalResults(filtered)
      } catch {
        setModalResults([])
      } finally {
        setModalLoading(false)
      }
    },
    [excludedDocumentIds]
  )

  /** Debounce pretrage u modalu (~450 ms) da se API ne zove na svaki karakter. */
  useEffect(() => {
    if (!showAddModal) return
    const timeoutId = window.setTimeout(() => {
      setDebouncedModalQuery(modalSearchQuery.trim())
    }, 450)
    return () => window.clearTimeout(timeoutId)
  }, [modalSearchQuery, showAddModal])

  useEffect(() => {
    if (!showAddModal) return
    void loadModalResults(debouncedModalQuery)
  }, [showAddModal, debouncedModalQuery, loadModalResults])

  const openAddModal = () => {
    setModalSearchQuery('')
    setDebouncedModalQuery('')
    setModalResults([])
    setSelectedInModal(null)
    setShowAddModal(true)
  }

  const closeAddModal = () => {
    setShowAddModal(false)
    setSelectedInModal(null)
  }

  const closeDeleteConfirm = () => {
    setShowDeleteConfirm(false)
    setDeleteTargetId(null)
  }

  const confirmAddDocument = async () => {
    if (!selectedInModal) return
    setSaving(true)
    try {
      await documentService.addRelatedItem(documentId, selectedInModal.id)
      closeAddModal()
      await loadRelatedItems()
    } catch (error) {
      toastService.showError(t('error_creating_link'), error)
    } finally {
      setSaving(false)
    }
  }

  const removeRelatedItem = async (targetDocumentId: number) => {
    setSaving(true)
    try {
      await documentService.removeRelatedItem(documentId, targetDocumentId)
      await loadRelatedItems()
    } catch (error) {
      toastService.showError(t('error_deleting_link'), error)
    } finally {
      setSaving(false)
    }
  }

  const downloadRelatedDocument = async (targetDocumentId: number) => {
    try {
      setDownloadingId(targetDocumentId)
      await documentService.downloadDocumentFile(targetDocumentId)
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response?.status
      const message =
        status === 404
          ? t('error_document_not_found_download')
          : (t('error_downloading_documents') || 'Error downloading document')
      toastService.showError(message, error)
      if (status === 404) {
        loadRelatedItems()
      }
    } finally {
      setDownloadingId(null)
    }
  }

  return (
    <div className="custom-fields-panel document-detail-section-divider">
      <h6 className="fw-bold mb-3 text-muted">{t('related_items')}</h6>

      <div className="d-flex gap-2 align-items-center mb-3">
        <Form.Control
          size="sm"
          value={filterQuery}
          onChange={(e) => setFilterQuery(e.target.value)}
          placeholder={t('related_items_filter_placeholder')}
          className="flex-grow-1"
        />
        <Button size="sm" variant="primary" disabled={!canEdit || saving} onClick={openAddModal} className="related-items-add-btn">
          {saving && <Spinner animation="border" size="sm" className="me-1" />}
          {t('add_item')}
        </Button>
      </div>

      <Modal show={showAddModal} onHide={closeAddModal} size="lg" centered dialogClassName="related-items-add-modal">
        <Modal.Header className="d-flex align-items-center">
          <Modal.Title className="mb-0">{t('related_items_add_modal_title')}</Modal.Title>
          <Button variant="outline-secondary" size="sm" className="ms-auto related-items-modal-close" onClick={closeAddModal} aria-label={t('close')}>
            <i className="bi bi-x-lg" aria-hidden />
          </Button>
        </Modal.Header>
        <Modal.Body className="related-items-add-modal-body">
          <Form.Control
            size="sm"
            className="mb-3"
            value={modalSearchQuery}
            onChange={(e) => setModalSearchQuery(e.target.value)}
            placeholder={t('search_for_documents')}
            autoComplete="off"
          />
          <div className="related-items-modal-results" aria-busy={modalLoading}>
            {modalLoading && (
              <div className="related-items-modal-results__overlay">
                <Spinner animation="border" size="sm" className="me-2" role="status" />
                <span className="text-muted small">{t('loading')}</span>
              </div>
            )}
            <ListGroup className="related-items-modal-results__list">
              {!modalLoading && modalResults.length === 0 && (
                <ListGroup.Item className="small text-muted border-0 rounded-0 bg-transparent">
                  {t('no_documents_found')}
                </ListGroup.Item>
              )}
              {modalResults.map((doc) => (
                <ListGroup.Item
                  key={doc.id}
                  action
                  active={selectedInModal?.id === doc.id}
                  onClick={() => setSelectedInModal(doc)}
                  className="d-flex justify-content-between align-items-center"
                >
                  <span className="fw-semibold">{doc.title || `#${doc.id}`}</span>
                  <span className="small text-muted">{formatDate(doc.created || '')}</span>
                </ListGroup.Item>
              ))}
            </ListGroup>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeAddModal}>
            {t('cancel')}
          </Button>
          <Button variant="primary" disabled={!selectedInModal || saving} onClick={confirmAddDocument}>
            {saving && <Spinner animation="border" size="sm" className="me-1" />}
            {t('confirm')}
          </Button>
        </Modal.Footer>
      </Modal>

      {loading ? (
        <div className="small text-muted">
          <Spinner animation="border" size="sm" className="me-2" />
          {t('loading')}
        </div>
      ) : (
        <ListGroup>
          {filteredItems.length === 0 && (
            <ListGroup.Item className="small text-muted">
              {filterQuery.trim() ? t('no_documents_found') : t('no_documents_found')}
            </ListGroup.Item>
          )}
          {filteredItems.map((item) => {
            const isSefOnly = item.relation_sources.includes('sef_auto') && !item.editable
            const isDisabled = !item.editable || saving
            return (
              <ListGroup.Item key={item.document_id} className="d-flex justify-content-between align-items-center">
                <div className="d-flex flex-column flex-grow-1 min-width-0">
                  <span className="fw-semibold">{item.title || `#${item.document_id}`}</span>
                  <div className="small text-muted">
                    {item.created ? formatDate(item.created) : ''}
                    {item.relation_sources.map((source) => (
                      <span key={source} className="badge text-bg-light ms-2">
                        {source === 'sef_auto' ? 'SEF' : t('manual')}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="d-flex gap-1 flex-shrink-0">
                  <Link
                    to={`/documents/${item.document_id}/details`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-sm btn-outline-primary"
                    title={t('preview')}
                    aria-label={t('preview')}
                  >
                    <i className="bi bi-eye" aria-hidden />
                  </Link>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    disabled={downloadingId === item.document_id}
                    title={t('download')}
                    aria-label={t('download')}
                    onClick={() => downloadRelatedDocument(item.document_id)}
                  >
                    {downloadingId === item.document_id ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <i className="bi bi-download" aria-hidden />
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant={isSefOnly ? 'outline-secondary' : 'outline-danger'}
                    disabled={!isSefOnly && isDisabled}
                    title={isSefOnly ? t('sef_relation_cannot_delete_tooltip') : undefined}
                    onClick={() => {
                      if (isSefOnly || isDisabled) return
                      setDeleteTargetId(item.document_id)
                      setShowDeleteConfirm(true)
                    }}
                    className={isSefOnly ? 'related-items-sef-delete-btn' : ''}
                  >
                    <span className={isSefOnly ? 'related-items-sef-delete-icon' : ''}>
                      <i className="bi bi-trash" aria-hidden />
                    </span>
                  </Button>
                </div>
              </ListGroup.Item>
            )
          })}
        </ListGroup>
      )}

      <Modal show={showDeleteConfirm} onHide={closeDeleteConfirm} centered>
        <Modal.Header className="d-flex align-items-center">
          <Modal.Title className="mb-0">{t('confirm_delete')}</Modal.Title>
          <Button variant="outline-secondary" size="sm" className="ms-auto related-items-modal-close" onClick={closeDeleteConfirm} aria-label={t('close')}>
            <i className="bi bi-x-lg" aria-hidden />
          </Button>
        </Modal.Header>
        <Modal.Body>{t('confirm_delete_related_item')}</Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeDeleteConfirm}>
            {t('cancel')}
          </Button>
          <Button
            variant="danger"
            onClick={async () => {
              if (deleteTargetId == null) return
              await removeRelatedItem(deleteTargetId)
              closeDeleteConfirm()
            }}
          >
            {t('confirm')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
